import Papa from 'papaparse';
import type { CsvRow, AppMessage } from '../tools/types';

export const MAX_ROWS       = 1_000;
export const MAX_FILE_BYTES = 3 * 1024 * 1024;
export const REQUIRED_COLS  = ['name', 'date', 'type'] as const;

export function sanitize(str: unknown): string {
  if (str == null) return '';
  return String(str)
    .replace(/<[^>]*>/g, '')           // strip HTML tags
    .replace(/[^\w\s@.,:()\-/]/g, '')  // allow safe chars only
    .trim()
    .slice(0, 500);
}

export function sanitizeDescription(str: unknown): string {
  if (str == null) return '';
  return String(str)
    .replace(/</g, '::lt::')
    .replace(/>/g, '::gt::')
    .replace(/[^\w\s@.,:()\-/]/g, '')  // allow safe chars only
    .trim()
    .slice(0, 500);
}

export function parseDate(str: string): Date | null {
  if (!str) return null;
  const s = str.trim();

  // yyyy-mm-dd format found
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // traditional mm/dd/yyyy format; not dd/mm/yyyy
  const dmyMatch = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (dmyMatch) {
    const [, m, d, y] = dmyMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // allow for "Mar 15, 2024" / "15 March 2024" / "March 15 2024" to continue on
  const fallback = new Date(s);
  if (!isNaN(fallback.getTime())) return fallback;

  return null;
}

export function formatDate(str: string): string {
  const d = parseDate(str);
  if (!d) return str || '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name || '?').slice(0, 2).toUpperCase();
}

export function processLookupLink(name: string): string {
    const link = 'https://intermountainhealthib.staywellsolutionsonline.com/Search/SearchResults.pg?SearchType=text&SearchOperator=And&SearchPhrase=' + encodeURIComponent(name);

    return link;
}

export interface ParseResult {
  rows: CsvRow[];
  extraCols: string[];
  messages: AppMessage[];
  fatal: boolean;
}

export function parseCsvFile(file: File): Promise<ParseResult> {
  return new Promise((resolve) => {
    const messages: AppMessage[] = [];

    // Pre-parse validations
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return resolve({ rows: [], extraCols: [], fatal: true, messages: [
        { type: 'error', text: 'Invalid file type. Please upload a .csv file.' },
      ]});
    }
    if (file.size > MAX_FILE_BYTES) {
      return resolve({ rows: [], extraCols: [], fatal: true, messages: [
        { type: 'error', text: `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 5 MB.` },
      ]});
    }

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete(res) {
        const fields = res.meta.fields ?? [];
        const lowerFields = fields.map(f => f.trim().toLowerCase());

        const missing = REQUIRED_COLS.filter(c => !lowerFields.includes(c));
        if (missing.length) {
          return resolve({ rows: [], extraCols: [], fatal: true, messages: [
            { type: 'error', text: `Missing required column(s): ${missing.map(m => m[0].toUpperCase() + m.slice(1)).join(', ')}. Your CSV must contain both a Name and a Date column.` },
          ]});
        }

        const raw = res.data;
        if (raw.length === 0) {
          return resolve({ rows: [], extraCols: [], fatal: true, messages: [
            { type: 'error', text: 'The CSV file contains no data rows.' },
          ]});
        }

        if (raw.length > MAX_ROWS) {
          messages.push({ type: 'warning', text: `Only the first ${MAX_ROWS.toLocaleString()} rows are loaded (file has ${raw.length.toLocaleString()}).` });
        }

        const nameKey = fields.find(f => f.trim().toLowerCase() === 'name')!;
        const dateKey = fields.find(f => f.trim().toLowerCase() === 'date')!;
        const typeKey = fields.find(f => f.trim().toLowerCase() === 'type');
        const extraCols = fields.filter(f => !['name', 'date', 'type'].includes(f.trim().toLowerCase()));

        let emptyNames = 0;
        let badDates   = 0;

        const rows: CsvRow[] = raw.slice(0, MAX_ROWS).map((r, i) => {
          const name = sanitize(r[nameKey]);
          const date = sanitize(r[dateKey]);
          const type = typeKey ? sanitize(r[typeKey]) : ''; 
          if (!name) emptyNames++;
          if (date && !parseDate(date)) badDates++;
          const extra: Record<string, string> = {};
          extraCols.forEach(c => { extra[c] = (c === "Description") ? sanitizeDescription(r[c]) : sanitize(r[c]); });
          return { _id: i, name, date, parsedDate: parseDate(date), type, extra };
        });

        if (emptyNames > 0) messages.push({ type: 'warning', text: `${emptyNames} row(s) have empty Name values.` });
        if (badDates > 0)   messages.push({ type: 'warning', text: `${badDates} row(s) have unrecognized date formats and are displayed as-is.` });
        messages.push({ type: 'success', text: `Loaded ${rows.length.toLocaleString()} rows from "${file.name}".` });

        resolve({ rows, extraCols, messages, fatal: false });
      },
      error(err) {
        resolve({ rows: [], extraCols: [], fatal: true, messages: [
          { type: 'error', text: `CSV parse error: ${err.message}` },
        ]});
      },
    });
  });
}
